import cubboLogo from '../../assets/logo-2.png'
import { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Notification } from '../../components'
import useAuth from '../../hooks/useAuth'
import { getLoginMethod } from '../../services/loginServices'

// Which copy each failure gets. Anything not listed falls back to the generic
// credentials message, so an unrecognised backend code can never surface raw.
const ERROR_COPY = {
  USER_DISABLED: 'login.account_disabled',
  RATE_LIMITED: 'login.too_many_attempts',
}

// Backend-mediated SSO: a full-page nav (it 302s to Auth0), not an SPA route.
// The address typed at the form is sent so the backend can record it in the SSO
// state and refuse a session for anyone else. Without it Auth0 answers with
// whatever Google session the browser already holds, and on a shared terminal you
// are signed in as the previous person.
const ssoStartUrl = (returnUrl, email) =>
  `${process.env.REACT_APP_PLATFORM_URL}/api/v1/auth/sso/start?app=wms&returnUrl=${encodeURIComponent(returnUrl)}` +
  (email ? `&email=${encodeURIComponent(email)}` : '')

export const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({})

  const history = useHistory()
  const query_string = useLocation().search
  const sso_error = new URLSearchParams(query_string).get('sso_error')

  // Identifier-first: step "email" → Continuar → either SSO redirect or step "password".
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [continuing, setContinuing] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [errorCode, setErrorCode] = useState(null)
  const { t } = useTranslation()

  const [loginUser, isLoading, isError] = useAuth()

  useEffect(() => {
    if (sso_error) setShowNotification(true)
  }, [sso_error])

  // Deep-link round-trip (e.g. the handheld /hh?code=... QR): PrivateRoute sends
  // logged-out users here with ?next=<original path>. Honor it on every exit —
  // password success AND both SSO redirects (returnUrl survives the Auth0 hop).
  const getNextPath = () => {
    const next = new URLSearchParams(query_string).get('next')
    if (next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\')) {
      return next
    }
    return '/select-warehouse'
  }

  const handleContinue = async e => {
    e.preventDefault()
    if (!email) return
    setContinuing(true)
    try {
      const method = await getLoginMethod(email)
      if (method === 'sso') {
        window.location.assign(ssoStartUrl(getNextPath(), email))
        return
      }
      setStep('password')
    } catch (err) {
      // Discovery being down must never brick login: fall open to the
      // password step — an SSO-enforced account still gets bounced by the
      // backend's 403 sso_required on submit.
      setStep('password')
    } finally {
      setContinuing(false)
    }
  }

  const onSubmit = data => {
    const { password } = data
    loginUser({ email, password })
      .then(() => {
        history.push(getNextPath())
      })
      .catch(e => {
        // Safety net: a @cubbo.com user who reached the password field anyway is
        // told by the backend (403 sso_required) to use SSO — bounce them there.
        if (e?.response?.data?.code === 'sso_required') {
          window.location.assign(ssoStartUrl(getNextPath(), email))
          return
        }
        // Only a code we recognise changes the copy. Rendering e.response.data.error
        // verbatim would put backend strings like "unexpected error, timestamp: …"
        // in front of an operator.
        // Rack::Attack answers 429 from middleware with no body code, and it
        // is keyed per email at 10 per 3 minutes — so someone retrying during
        // an incident locks themselves out and gets told their password is
        // wrong. Map the status to a code of our own before falling back.
        setErrorCode(e?.response?.status === 429 ? 'RATE_LIMITED' : (e?.response?.data?.code ?? null))
        setShowNotification(true)
      })
  }

  const handleFormSubmit = e => {
    if (step === 'email') return handleContinue(e)
    return handleSubmit(onSubmit)(e)
  }

  return (
    <div className="min-h-screen bg-secondary-orange-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={cubboLogo} alt="Workflow" />
      </div>

      <div className="mt-16 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <h2 className="mt-3 mb-3 text-center text-2xl text-gray-900">Ingresa al WMS</h2>
          <p className="text-center text-gray-500 mb-12">Por favor ingresa los datos a continuación</p>
          {sso_error && showNotification ? (
            <Notification
              show={showNotification}
              setShow={setShowNotification}
              type="error"
              title="Error"
              description={
                // no_account means Google accepted them but we hold no user row for
                // that address — usually their real account is operadores+name@.
                // "Try again" is useless advice for that; naming it is not.
                sso_error === 'no_account' ? t('login.sso_no_account') : t('login.sso_failed')
              }
            />
          ) : null}
          <form action="#" method="POST" className="space-y-6" onSubmit={handleFormSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Usuario o email
              </label>
              <div className="mt-1">
                <input
                  name="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={step === 'password'}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              {step === 'password' ? (
                <button type="button" onClick={() => setStep('email')} className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  Usar otro usuario
                </button>
              ) : null}
            </div>

            {step === 'password' ? (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Contraseña
                </label>
                <div className="mt-1">
                  <input
                    name="password"
                    type="password"
                    autoFocus
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    {...register('password', {
                      required: 'Este campo es requerido',
                    })}
                  />
                  <p className="mt-2 text-sm text-red-600">{errors.password && errors.password.message}</p>
                </div>
              </div>
            ) : null}

            <div>
              {step === 'email' ? (
                <button
                  type="submit"
                  disabled={continuing}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Continuar
                </button>
              ) : isLoading ? (
                'Ingresando...'
              ) : (
                <>
                  {isError && showNotification ? (
                    <>
                      <Notification
                        show={showNotification}
                        setShow={setShowNotification}
                        type="error"
                        title="Error"
                        description={t(ERROR_COPY[errorCode] ?? 'login.invalid_credentials')}
                      />
                    </>
                  ) : null}
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Ingresa
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
